import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Workflow,
  Mail,
  Users,
  BarChart3,
  FileText,
  Settings,
  LogOut,
  Megaphone,
  CreditCard,
  Building2,
  BookOpen,
  Activity,
  MapPin,
  TrendingUp,
  Target,
  Search,
  Store,
  LineChart,
  BarChart2,
  QrCode,
  Lightbulb,
  Bot,
  Wand2,
  TrendingUp as TrendingUpIcon,
  FlaskConical,
  Sparkles,
  Zap,
  ChevronDown,
  ChevronRight,
  Gauge,
  PenTool,
  Globe,
  Wrench,
  Phone,
  ShieldAlert,
  Facebook,
  Tag
} from 'lucide-react';

interface NavItem {
  name: string;
  href?: string;
  icon: any;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { 
    name: 'Dashboard', 
    href: '/admin', 
    icon: LayoutDashboard 
  },
  {
    name: 'AI & Automation',
    icon: Sparkles,
    children: [
      { name: 'AI Agent Monitor', href: '/admin/ai-agent', icon: Bot },
      { name: 'AI Agent Workflow', href: '/admin/ai-agent-workflow', icon: Bot },
      { name: 'Automation Engine', href: '/admin/automation-engine', icon: Zap },
      { name: 'Lead Scoring', href: '/admin/lead-scoring', icon: TrendingUpIcon },
      { name: 'Predictive Maintenance', href: '/admin/predictive-maintenance', icon: Wrench },
      { name: 'Nurture Sequences', href: '/admin/nurture-sequences', icon: Mail },
      { name: 'AI Campaign Generator', href: '/admin/campaign-generator', icon: Wand2 },
      { name: 'A/B Testing Hub', href: '/admin/ab-testing', icon: FlaskConical },
    ]
  },
  {
    name: 'Marketing Campaigns',
    icon: Megaphone,
    children: [
      { name: 'Campaigns', href: '/admin/campaigns', icon: Megaphone },
      { name: 'QR Tracking', href: '/admin/qr-tracking', icon: QrCode },
      { name: 'Workflows', href: '/admin/workflows', icon: Workflow },
      { name: 'Segments', href: '/admin/segments', icon: Users },
      { name: 'Templates', href: '/admin/templates', icon: Mail },
      { name: 'Customers', href: '/admin/customers', icon: Users },
      { name: 'Do Not Contact', href: '/admin/dnc', icon: ShieldAlert },
    ]
  },
  {
    name: 'Advertising',
    icon: Megaphone,
    children: [
      { name: 'Google Ads Manager', href: '/admin/google-ads', icon: Megaphone },
      { name: 'Automated Bid Manager', href: '/admin/google-ads/bids', icon: Zap },
      { name: 'Quality Score Monitor', href: '/admin/google-ads/quality-score', icon: Gauge },
      { name: 'Search Query Analyzer', href: '/admin/google-ads/search-queries', icon: Search },
      { name: 'Negative Keyword Manager', href: '/admin/google-ads/negative-keywords', icon: Target },
      { name: 'Campaign Health Monitor', href: '/admin/google-ads/health', icon: Activity },
      { name: 'Budget Optimizer', href: '/admin/google-ads/budget', icon: CreditCard },
      { name: 'Conversion Tracking', href: '/admin/google-ads/conversions', icon: BarChart3 },
      { name: 'Reporting Suite', href: '/admin/google-ads/reporting', icon: FileText },
      { name: 'Ad Copy Performance', href: '/admin/google-ads/ad-copy-performance', icon: PenTool },
      { name: 'Auction Insights', href: '/admin/google-ads/auction-insights', icon: LineChart },
      { name: 'Shopping Manager', href: '/admin/google-ads/shopping', icon: Store },
      { name: 'Display Manager', href: '/admin/google-ads/display', icon: LayoutDashboard },
      { name: 'Attribution', href: '/admin/google-ads/attribution', icon: TrendingUp },
      { name: 'LSA Manager', href: '/admin/lsa-manager', icon: Target },
    ]
  },
  {
    name: 'Analytics & Intelligence',
    icon: BarChart3,
    children: [
      { name: 'Analytics Dashboard', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Analytics & Insights', href: '/admin/analytics-insights', icon: Target },
      { name: 'Market Intelligence', href: '/admin/market-intelligence', icon: TrendingUp },
      { name: 'Keyword Demand', href: '/admin/keyword-demand', icon: Search },
      { name: 'SEO Intelligence Dashboard', href: '/admin/seo-intelligence', icon: Gauge },
      { name: 'SERP Tracking', href: '/admin/serp-tracking', icon: LineChart },
      { name: 'Share of Search', href: '/admin/share-of-search', icon: BarChart2 },
      { name: 'Content Gap Analysis', href: '/admin/content-gap', icon: Lightbulb },
    ]
  },
  {
    name: 'Performance Monitoring',
    icon: Gauge,
    children: [
      { name: 'Web Vitals', href: '/admin/web-vitals', icon: Activity },
      { name: 'GBP Performance', href: '/admin/gbp-dashboard', icon: Store },
      { name: 'Reviews Monitor', href: '/admin/reviews-monitor', icon: Store },
      { name: 'Competitor Heatmap', href: '/admin/competitor-heatmap', icon: MapPin },
      { name: 'Call Performance', href: '/admin/call-performance', icon: Phone },
    ]
  },
  {
    name: 'Content & SEO',
    icon: PenTool,
    children: [
      { name: 'Blog Automation', href: '/admin/blog-automation', icon: FileText },
      { name: 'Facebook Manager', href: '/admin/facebook-manager', icon: Facebook },
      { name: 'GBP Manager', href: '/admin/gbp-manager', icon: Store },
      { name: 'WordPress SEO', href: '/admin/wordpress-seo', icon: Globe },
      { name: 'Service Area Hubs', href: '/admin/service-area-hubs', icon: MapPin },
      { name: 'Knowledge Base', href: '/admin/knowledge-base', icon: BookOpen },
    ]
  },
  {
    name: 'Business Management',
    icon: Building2,
    children: [
      { name: 'Memberships', href: '/admin/memberships', icon: CreditCard },
      { name: 'Membership Management', href: '/admin/membership-management', icon: Users },
      { name: 'Promo Codes', href: '/admin/promo-codes', icon: Tag },
      { name: 'Company Info', href: '/admin/company-info', icon: Building2 },
    ]
  },
  { 
    name: 'System Documentation', 
    href: '/admin/documentation', 
    icon: BookOpen 
  },
  { 
    name: 'Settings', 
    href: '/admin/settings', 
    icon: Settings 
  },
];

export default function Layout() {
  const location = useLocation();
  const { marketingUser, signOut } = useAuth();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    'AI & Automation': false,
    'Marketing Campaigns': false,
    'Advertising': false,
    'Analytics & Intelligence': false,
    'Performance Monitoring': false,
    'Content & SEO': false,
    'Business Management': false,
  });

  const toggleSection = (sectionName: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Get active section for highlighting
  const getActiveSection = () => {
    const path = location.pathname;
    for (const item of navigation) {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.href === path);
        if (hasActiveChild) return item.name;
      }
    }
    return null;
  };
  const activeSection = getActiveSection();

  // Auto-open the active section so the user can see where they are in the nav.
  useEffect(() => {
    if (!activeSection) return;
    setOpenSections((prev) => {
      if (prev[activeSection]) return prev;
      return { ...prev, [activeSection]: true };
    });
  }, [activeSection]);

  const renderNavItem = (item: NavItem) => {
    // If item has children, render as collapsible section
    if (item.children) {
      const isOpen = openSections[item.name];
      const hasActiveChild = item.children.some(child => child.href === location.pathname);
      const sectionId = `nav-section-${item.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

      return (
        <div key={item.name} className="space-y-1">
          <button
            onClick={() => toggleSection(item.name)}
            aria-expanded={isOpen}
            aria-controls={sectionId}
            className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 w-full group ${
              hasActiveChild
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg border border-blue-500/30'
                : 'text-gray-300 hover:bg-white/10 hover:text-white hover:scale-[1.02] backdrop-blur-sm border border-transparent hover:border-white/20'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-colors ${
              hasActiveChild ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/15'
            }`}>
              <item.icon className="w-5 h-5" />
            </div>
            <span className="flex-1 text-left font-medium">{item.name}</span>
            {isOpen ? (
              <ChevronDown className="w-4 h-4 transition-transform duration-200" />
            ) : (
              <ChevronRight className="w-4 h-4 transition-transform duration-200" />
            )}
          </button>

          {isOpen && (
            <div id={sectionId} className="ml-6 space-y-1 mt-2">
              {item.children.map(child => {
                const isChildActive = location.pathname === child.href;
                return (
                  <Link
                    key={child.name}
                    to={child.href!}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm group ${
                      isChildActive
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md border border-indigo-500/30'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white hover:scale-[1.01] hover:translate-x-1 border border-transparent hover:border-white/10'
                    }`}
                  >
                    <div className={`p-1 rounded-md transition-colors ${
                      isChildActive ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/15'
                    }`}>
                      <child.icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-medium">{child.name}</span>
                    {isChildActive && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // If item has no children, render as direct link
    const isActive = location.pathname === item.href;
    return (
      <Link
        key={item.name}
        to={item.href!}
        className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 group ${
          isActive
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg border border-blue-500/30 transform scale-[1.02]'
            : 'text-gray-300 hover:bg-white/10 hover:text-white hover:scale-[1.02] backdrop-blur-sm border border-transparent hover:border-white/20'
        }`}
      >
        <div className={`p-1.5 rounded-lg transition-colors ${
          isActive ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/15'
        }`}>
          <item.icon className="w-5 h-5" />
        </div>
        <span className="font-medium">{item.name}</span>
        {isActive && (
          <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse shadow-sm"></div>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-purple-500/3 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* Premium Sidebar */}
        <div className="w-80 bg-gray-900/95 backdrop-blur-xl border-r border-white/10 text-white flex flex-col shadow-2xl">
          {/* RAHS Branding Header */}
          <div className="p-8 border-b border-white/10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  RAHS Marketing
                </h1>
                <p className="text-blue-200 text-sm">Enterprise Platform</p>
              </div>
            </div>

            {marketingUser && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <p className="text-sm text-gray-300">
                  Welcome back,
                </p>
                <p className="font-semibold text-white">
                  {marketingUser.first_name} {marketingUser.last_name}
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-6 space-y-2 overflow-y-auto">
            <div className="mb-6">
              <p className="text-blue-200 font-semibold px-3 py-2 text-sm uppercase tracking-wider border-b border-white/10 mb-4">
                Main Navigation
              </p>
              {navigation.filter(item => !item.children).map(renderNavItem)}
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-2">
              {navigation.filter(item => item.children).map(renderNavItem)}
            </div>
          </nav>

          {/* Sign Out Footer */}
          <div className="p-6 border-t border-white/10">
            <button
              onClick={signOut}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-medium transition-all duration-200 hover:scale-105 shadow-lg"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out Securely</span>
            </button>
          </div>
        </div>

        {/* Premium Main Content */}
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-blue-50/50 min-h-screen overflow-auto">
          <div className="relative">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
